import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { LanguagePicker } from '../LanguagePicker';
import { HankoStamp } from '../ui/HankoStamp';
import { PlyEZMark } from '../BrandLogo';

/**
 * Two-pane auth layout. Left: brand mark, title, the form. Right: a calm
 * washi-paper showcase with the same product cards from the hero, so a
 * first-time visitor sees the product even on the signup screen.
 */
export function AuthLayout({
  eyebrow,
  title,
  subtitle,
  children,
  altCta,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  children: ReactNode;
  altCta?: { label: string; to: string; helper?: string };
}) {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen flex">
      {/* Form pane */}
      <div className="flex-1 flex flex-col px-6 sm:px-10 py-8 bg-background">
        <header className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <PlyEZMark size={28} />
            <span className="font-display text-lg font-semibold">{t('brand')}</span>
          </Link>
          <LanguagePicker compact />
        </header>

        <div className="flex-1 flex items-center">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="w-full max-w-md mx-auto"
          >
            {eyebrow && (
              <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium mb-3">
                {eyebrow}
              </div>
            )}
            <h1 className="display text-3xl font-semibold tracking-tight">{title}</h1>
            {subtitle && <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{subtitle}</p>}
            <div className="mt-8">{children}</div>
            {altCta && (
              <p className="mt-8 text-center text-sm text-muted-foreground">
                {altCta.helper && <>{altCta.helper} </>}
                <Link to={altCta.to} className="text-accent-700 font-medium hover:underline">
                  {altCta.label}
                </Link>
              </p>
            )}
          </motion.div>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">{t('footer.copyright')}</p>
      </div>

      {/* Showcase pane — hidden on small screens */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden border-l border-border bg-gradient-to-br from-muted via-background to-pearl-100 washi">
        <motion.div
          className="absolute -top-20 -right-12 h-96 w-96 rounded-full blur-3xl"
          style={{ background: 'radial-gradient(circle, hsl(219 55% 27% / 0.22), transparent 60%)' }}
          animate={{ y: [0, 18, 0], x: [0, 10, 0] }}
          transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-0 -left-20 h-[28rem] w-[28rem] rounded-full blur-3xl"
          style={{ background: 'radial-gradient(circle, hsl(351 78% 43% / 0.16), transparent 60%)' }}
          animate={{ y: [0, -22, 0], x: [0, 8, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
        />

        <div className="relative z-10 flex flex-col justify-center p-12 max-w-xl">
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-3 flex items-center gap-2">
            <span className="sparkle-dot" aria-hidden />
            {t('landing.heroEyebrow')}
          </div>
          <h2 className="display text-3xl lg:text-4xl font-semibold leading-tight text-foreground text-balance">
            {t('landing.heroHeadlineTop')}{' '}
            <span className="underline-cinnabar">{t('landing.heroHeadlineHighlight')}</span>
            {t('landing.heroHeadlineTail')}
          </h2>
          <p className="mt-5 text-muted-foreground leading-relaxed">{t('subtag')}</p>

          <div className="mt-10 rounded-xl bg-card border border-border p-4 shadow-soft">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                  {t('landing.heroCardRingiLabel')}
                </div>
                <div className="font-medium mt-0.5">{t('landing.heroCardRingiFlow')}</div>
              </div>
              <div className="flex gap-2">
                <HankoStamp text="田中" size={42} />
                <HankoStamp text="佐藤" size={42} animate />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
